/**
 * Client grouping for the dashboard sidebar (platform-admin view).
 *
 * The admin's /api/v2/sites list spans every tenant, so a flat project list
 * grows unreadable. The sidebar instead shows one row per client and opens
 * a per-client project panel on click. Pure helper so the grouping and its
 * badge/meta arithmetic are unit-testable without React.
 */

import type { SiteListItem } from '../../services/projectApi';
import type { AlarmCount } from './DashboardMap';

/** Group key for sites whose `client_id` is missing (legacy rows). */
export const UNASSIGNED_CLIENT_ID = '__unassigned__';

export interface ClientGroup {
  id: string;
  name: string;
  /** Server order preserved (updated_at DESC). */
  projects: SiteListItem[];
  /** Active alarms summed across the client's projects — drives the row badge. */
  critical: number;
  warning: number;
  /** Projects with no map pin yet, surfaced in the row meta so the admin
   *  can see which clients still need "Set location" work. */
  unpinned: number;
}

export function clientKeyOf(site: Pick<SiteListItem, 'client_id'>): string {
  return site.client_id || UNASSIGNED_CLIENT_ID;
}

export function clientNameOf(site: Pick<SiteListItem, 'client_id' | 'client_name'>): string {
  if (!site.client_id) return 'Unassigned';
  return site.client_name?.trim() || 'Unnamed client';
}

export function groupProjectsByClient(
  projects: SiteListItem[],
  alarmCounts: Record<string, AlarmCount>
): ClientGroup[] {
  const byId = new Map<string, ClientGroup>();
  for (const site of projects) {
    const id = clientKeyOf(site);
    let group = byId.get(id);
    if (!group) {
      group = { id, name: clientNameOf(site), projects: [], critical: 0, warning: 0, unpinned: 0 };
      byId.set(id, group);
    }
    group.projects.push(site);
    const counts = alarmCounts[site.id];
    if (counts) {
      group.critical += counts.critical ?? 0;
      group.warning += counts.warning ?? 0;
    }
    if (typeof site.latitude !== 'number' || typeof site.longitude !== 'number') {
      group.unpinned += 1;
    }
  }
  const groups = Array.from(byId.values());
  groups.sort((a, b) => {
    // Named clients A→Z; the legacy "Unassigned" bucket always sinks to the end.
    if (a.id === UNASSIGNED_CLIENT_ID) return 1;
    if (b.id === UNASSIGNED_CLIENT_ID) return -1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
  });
  return groups;
}
